// ============================================
// 📄 GERADOR DE PDF - RELATÓRIOS DE AGENDA
// ============================================
import { Agendamento } from "../context/AgendaContext";
// 🔒 B317 (AI_143, achado A1 da auditoria externa) — todo valor de DADO
// interpolado no HTML de impressão passa por escapeHtml (fonte única).
import { escapeHtml } from "../../../shared/utils/escapeHtml";

const DIAS_SEMANA = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];
const MESES = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

const getLabelStatus = (status: string) => {
  switch (status) {
    case "agendado":
      return "Agendado";
    case "realizado":
      return "✓ Realizado";
    case "nao_realizado":
      return "✗ Não realizado";
    case "remarcado":
      return "↻ Remarcado";
    case "cancelado":
      return "Cancelado";
    default:
      return status;
  }
};

const getCorStatus = (status: string) => {
  switch (status) {
    case "agendado":
      return "#1e40af";
    case "realizado":
      return "#166534";
    case "nao_realizado":
      return "#991b1b";
    case "remarcado":
      return "#92400e";
    case "cancelado":
      return "#6b7280";
    default:
      return "#6b7280";
  }
};

// ============================================
// RELATÓRIO DA AGENDA DO DIA
// ============================================
export function gerarPDFAgendaDia(
  data: string,
  agendamentos: Agendamento[],
  profissionalNome?: string
) {
  const dataObj = new Date(data + "T00:00:00");
  const diaSemana = DIAS_SEMANA[dataObj.getDay()];
  const dataFormatada = `${String(dataObj.getDate()).padStart(2, "0")} de ${
    MESES[dataObj.getMonth()]
  } de ${dataObj.getFullYear()}`;

  const agOrdenados = [...agendamentos].sort((a, b) =>
    a.hora.localeCompare(b.hora)
  );
  const realizados = agOrdenados.filter((a) => a.status === "realizado").length;
  const naoRealizados = agOrdenados.filter(
    (a) => a.status === "nao_realizado"
  ).length;
  const agendados = agOrdenados.filter((a) => a.status === "agendado").length;

  // Agrupar por profissional
  const porProfissional = new Map<string, Agendamento[]>();
  agOrdenados.forEach((ag) => {
    const lista = porProfissional.get(ag.profissionalNome) || [];
    lista.push(ag);
    porProfissional.set(ag.profissionalNome, lista);
  });

  let tabelaHTML = "";

  porProfissional.forEach((lista, profNome) => {
    tabelaHTML += `
      <div style="margin-bottom: 20px;">
        <h3 style="font-size: 14px; font-weight: 700; color: #1e40af; margin-bottom: 8px; border-bottom: 2px solid #1e40af; padding-bottom: 4px;">
          ${escapeHtml(profNome)}
        </h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
          <thead>
            <tr style="background: #f1f5f9;">
              <th style="padding: 6px 8px; text-align: left; border: 1px solid #e2e8f0; width: 60px;">Hora</th>
              <th style="padding: 6px 8px; text-align: left; border: 1px solid #e2e8f0;">Paciente</th>
              <th style="padding: 6px 8px; text-align: left; border: 1px solid #e2e8f0; width: 80px;">Tipo</th>
              <th style="padding: 6px 8px; text-align: center; border: 1px solid #e2e8f0; width: 100px;">Status</th>
              <th style="padding: 6px 8px; text-align: left; border: 1px solid #e2e8f0;">Obs.</th>
            </tr>
          </thead>
          <tbody>
            ${lista
              .map(
                (ag, i) => `
              <tr style="background: ${i % 2 === 0 ? "white" : "#f8fafc"};">
                <td style="padding: 6px 8px; border: 1px solid #e2e8f0; font-weight: 700;">${
                  escapeHtml(ag.hora)
                }</td>
                <td style="padding: 6px 8px; border: 1px solid #e2e8f0;">${
                  escapeHtml(ag.pacienteNome)
                }</td>
                <td style="padding: 6px 8px; border: 1px solid #e2e8f0; font-size: 11px;">${
                  ag.tipo === "retorno"
                    ? "Retorno"
                    : ag.tipo === "primeira_consulta"
                    ? "1ª Consulta"
                    : "Consulta"
                }</td>
                <td style="padding: 6px 8px; border: 1px solid #e2e8f0; text-align: center; color: ${getCorStatus(
                  ag.status
                )}; font-weight: 600; font-size: 11px;">${escapeHtml(
                  getLabelStatus(ag.status)
                )}</td>
                <td style="padding: 6px 8px; border: 1px solid #e2e8f0; font-size: 10px; color: #6b7280;">${
                  escapeHtml(ag.observacao) || "—"
                }</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    `;
  });

  const html = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Agenda - ${dataFormatada}</title>
  <style>
    @page { size: A4 landscape; margin: 15mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
      color: #1a1a1a;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    @media print {
      body { margin: 0; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="text-align: center; padding: 12px; background: #f0f0f0;">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>

  <div style="max-width: 100%; margin: 0 auto; padding: 16px;">
    <!-- CABEÇALHO -->
    <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #2d6a2e; padding-bottom: 12px; margin-bottom: 16px;">
      <div>
        <h1 style="font-size: 20px; font-weight: 700; color: #2d6a2e; text-transform: uppercase;">
          Ambulatório de Transplante Hepático de Rondônia
        </h1>
        <h2 style="font-size: 16px; font-weight: 600; color: #333; margin-top: 4px;">
          Agenda de Consultas — ${diaSemana}, ${dataFormatada}
        </h2>
        ${
          profissionalNome
            ? `<p style="font-size: 13px; color: #666; margin-top: 2px;">Profissional: ${escapeHtml(profissionalNome)}</p>`
            : ""
        }
      </div>
      <div style="text-align: right; font-size: 12px; color: #666;">
        <div>Total: <strong>${agOrdenados.length}</strong> consulta(s)</div>
        <div style="color: #166534;">Realizadas: <strong>${realizados}</strong></div>
        <div style="color: #991b1b;">Não realizadas: <strong>${naoRealizados}</strong></div>
        <div style="color: #1e40af;">Pendentes: <strong>${agendados}</strong></div>
      </div>
    </div>

    <!-- TABELAS POR PROFISSIONAL -->
    ${tabelaHTML}

    <!-- RODAPÉ -->
    <div style="margin-top: 24px; padding-top: 12px; border-top: 2px solid #2d6a2e; text-align: center; font-size: 11px; color: #888;">
      Relatório gerado em ${new Date().toLocaleString("pt-BR")}
    </div>
  </div>
</body>
</html>
  `;

  abrirPDFEmNovaJanela(html);
}

// ============================================
// RELATÓRIO DE CONSULTAS REALIZADAS POR PERÍODO
// ============================================
export function gerarPDFConsultasRealizadas(
  dataInicio: string,
  dataFim: string,
  agendamentos: Agendamento[],
  profissionalNome?: string
) {
  const realizados = agendamentos
    .filter(
      (a) =>
        a.status === "realizado" && a.data >= dataInicio && a.data <= dataFim
    )
    .sort(
      (a, b) => a.data.localeCompare(b.data) || a.hora.localeCompare(b.hora)
    );

  const inicioFormatado = dataInicio.split("-").reverse().join("/");
  const fimFormatado = dataFim.split("-").reverse().join("/");

  // Agrupar por data
  const porData = new Map<string, Agendamento[]>();
  realizados.forEach((ag) => {
    const lista = porData.get(ag.data) || [];
    lista.push(ag);
    porData.set(ag.data, lista);
  });

  let conteudoHTML = "";
  porData.forEach((lista, dataStr) => {
    const dObj = new Date(dataStr + "T00:00:00");
    const diaSem = DIAS_SEMANA[dObj.getDay()];
    const dataBR = dataStr.split("-").reverse().join("/");

    conteudoHTML += `
      <div style="margin-bottom: 16px;">
        <h3 style="font-size: 13px; font-weight: 700; color: #333; margin-bottom: 6px; background: #f1f5f9; padding: 4px 8px; border-radius: 4px;">
          ${diaSem}, ${dataBR} — ${lista.length} consulta(s)
        </h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px;">
          <thead>
            <tr style="background: #f8fafc;">
              <th style="padding: 4px 8px; text-align: left; border: 1px solid #e2e8f0; width: 50px;">Hora</th>
              <th style="padding: 4px 8px; text-align: left; border: 1px solid #e2e8f0;">Paciente</th>
              <th style="padding: 4px 8px; text-align: left; border: 1px solid #e2e8f0;">Profissional</th>
              <th style="padding: 4px 8px; text-align: left; border: 1px solid #e2e8f0; width: 70px;">Tipo</th>
            </tr>
          </thead>
          <tbody>
            ${lista
              .map(
                (ag, i) => `
              <tr style="background: ${i % 2 === 0 ? "white" : "#f8fafc"};">
                <td style="padding: 4px 8px; border: 1px solid #e2e8f0; font-weight: 600;">${
                  escapeHtml(ag.hora)
                }</td>
                <td style="padding: 4px 8px; border: 1px solid #e2e8f0;">${
                  escapeHtml(ag.pacienteNome)
                }</td>
                <td style="padding: 4px 8px; border: 1px solid #e2e8f0; font-size: 11px;">${
                  escapeHtml(ag.profissionalNome)
                }</td>
                <td style="padding: 4px 8px; border: 1px solid #e2e8f0; font-size: 11px;">${
                  ag.tipo === "retorno"
                    ? "Retorno"
                    : ag.tipo === "primeira_consulta"
                    ? "1ª Consulta"
                    : "Consulta"
                }</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    `;
  });

  const html = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Consultas Realizadas - ${inicioFormatado} a ${fimFormatado}</title>
  <style>
    @page { size: A4; margin: 15mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
      color: #1a1a1a;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    @media print {
      body { margin: 0; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="text-align: center; padding: 12px; background: #f0f0f0;">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>

  <div style="max-width: 210mm; margin: 0 auto; padding: 16px;">
    <!-- CABEÇALHO -->
    <div style="border-bottom: 3px solid #2d6a2e; padding-bottom: 12px; margin-bottom: 16px;">
      <h1 style="font-size: 18px; font-weight: 700; color: #2d6a2e; text-transform: uppercase;">
        Ambulatório de Transplante Hepático de Rondônia
      </h1>
      <h2 style="font-size: 15px; font-weight: 600; color: #333; margin-top: 4px;">
        Relatório de Consultas Realizadas
      </h2>
      <p style="font-size: 13px; color: #666; margin-top: 2px;">
        Período: ${inicioFormatado} a ${fimFormatado}
        ${profissionalNome ? ` — Profissional: ${escapeHtml(profissionalNome)}` : ""}
      </p>
      <p style="font-size: 14px; color: #166534; font-weight: 700; margin-top: 4px;">
        Total de consultas realizadas: ${realizados.length}
      </p>
    </div>

    <!-- CONTEÚDO -->
    ${
      conteudoHTML ||
      '<p style="text-align: center; color: #999; padding: 32px;">Nenhuma consulta realizada neste período.</p>'
    }

    <!-- RODAPÉ -->
    <div style="margin-top: 24px; padding-top: 12px; border-top: 2px solid #2d6a2e; text-align: center; font-size: 11px; color: #888;">
      Relatório gerado em ${new Date().toLocaleString("pt-BR")}
    </div>
  </div>
</body>
</html>
  `;

  abrirPDFEmNovaJanela(html);
}

// ============================================
// ABRIR PDF EM NOVA JANELA
// ============================================
function abrirPDFEmNovaJanela(html: string) {
  const janela = window.open("", "_blank");
  if (janela) {
    janela.document.write(html);
    janela.document.close();
  }
}
